import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import Modal from "react-modal";
import StyledModal from "../Modal/StyledModal";
import LocalStorage from "../../../shared/localStorage";
import Axios from "../../../services/Axios";
import APIVariables from "../../../services/api-variables";
import { Auth } from "../../../routes/Constants";
import { ReactComponent as Warning } from "../../../assets/icons/warning.svg";
import { useAuth } from "../../../contexts/AuthContext";
import {
  setInterval as setWorkerInterval,
  clearInterval as clearWorkerInterval,
} from "worker-timers";

// You only need to adjust these two variables to change the timing of the session warning and expiration
const MAX_INACTIVITY_MINUTES = 15;
const WARNING_AFTER_INACTIVE_MINUTES = 5;

const COUNTDOWN_TIME =
  (MAX_INACTIVITY_MINUTES - WARNING_AFTER_INACTIVE_MINUTES) * 60;
const SESSION_CHECK_INTERVAL_MS = 60 * 1000;

const SessionWarning = () => {
  const { isLoggedIn } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [expired, setExpired] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(true);
  const [countdown, setCountdown] = useState(COUNTDOWN_TIME);
  const countdownIntervalRef = useRef(null);
  const keepAliveIntervalRef = useRef(null);
  const hasLoggedOutRef = useRef(false);

  const shouldShow = isLoggedIn;

  const redirectToLogin = useCallback(() => {
    window.location.href = Auth.LOGIN;
  }, []);

  const expireSession = useCallback(async () => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;

    try {
      await Axios.get(APIVariables.USERS_LOGOUT);
    } catch (error) {
      // Logout request failed, but continue with local session cleanup
      console.warn("Logout request failed:", error);
    }

    try {
      localStorage.removeItem(LocalStorage.USER_NHSPORTAL);
    } catch {
      console.warn("Catch 101");
    }

    setExpired(true);
    setShowWarning(false);
  }, []);

  const signOut = useCallback(async () => {
    await expireSession();
    redirectToLogin();
  }, [expireSession, redirectToLogin]);

  const idleTimer = useIdleTimer({
    timeout: MAX_INACTIVITY_MINUTES * 60 * 1000,
    promptBeforeIdle: COUNTDOWN_TIME * 1000,
    onIdle: () => {
      if (shouldShow) {
        expireSession();
      }
    },
    onPrompt: () => {
      if (refreshSuccess) {
        setShowWarning(true);
        setExpired(false);
      } else {
        expireSession();
      }
    },
    onActive: () => {
      if (!expired) setShowWarning(false);
    },
    events: ["keydown", "mousedown", "touchstart"],
    debounce: 500,
    crossTab: true,
    leaderElection: true,
    syncTimers: 3000,
    disabled: !shouldShow,
  });

  useEffect(() => {
    hasLoggedOutRef.current = false;
  }, [shouldShow]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        await Axios.get(APIVariables.USERS_PROFILE);
        setRefreshSuccess(true);
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setRefreshSuccess(false);
          await expireSession();
          return;
        }
        setRefreshSuccess(true);
      }
    };

    if (shouldShow && !expired) {
      checkSession();
      keepAliveIntervalRef.current = setWorkerInterval(
        checkSession,
        SESSION_CHECK_INTERVAL_MS
      );
    }

    return () => {
      if (keepAliveIntervalRef.current) {
        clearWorkerInterval(keepAliveIntervalRef.current);
      }
    };
  }, [shouldShow, expired, expireSession]);

  // countdown logic
  useEffect(() => {
    if (showWarning && !expired) {
      setCountdown(Math.ceil(idleTimer.getRemainingTime() / 1000));
      countdownIntervalRef.current = setWorkerInterval(() => {
        setCountdown(Math.ceil(idleTimer.getRemainingTime() / 1000));
      }, 1000);
    } else {
      clearWorkerInterval(countdownIntervalRef.current);
    }

    return () => {
      clearWorkerInterval(countdownIntervalRef.current);
    };
  }, [showWarning, expired, idleTimer]);

  if (!shouldShow && !showWarning && !expired) return null;

  return (
    <>
      <StyledModal>
        <Modal
          isOpen={showWarning || expired}
          onRequestClose={() => {
            if (!expired) setShowWarning(false);
          }}
          style={{
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              width: "520px",
              borderRadius: "12px",
            },
          }}
          contentLabel="Session timeout"
          overlayClassName="overlay-modal"
          shouldCloseOnOverlayClick={false}
          data={{ testid: "session-warning-modal" }}
        >
          <div className="wrapper-modal" data-testid="session-warning-modal-content">
            <div className="image">
              <Warning />
            </div>

            <div className="second-text" data-testid="session-warning-modal-message">
              {!expired ? (
                <div className="col2 flex-center">
                   <div
                     style={{ fontWeight: 600, fontSize: 28, textAlign: "center", padding: '20px 40px', backgroundColor: '#f5f5f5', borderRadius: 8, marginBottom: 12 }}
                     data-testid="session-warning-modal-countdown"
                   >
                    {String(Math.floor(Math.max(0, countdown) / 60)).padStart(2, "0")}:
                    {String(Math.max(0, countdown) % 60).padStart(2, "0")}
                  </div>
                  Your account has remained inactive for {WARNING_AFTER_INACTIVE_MINUTES} minutes. Please select 'Active' or you will be automatically signed out.
                </div>
              ) : (
                <>Your session has expired. Please click the button below to return to the login page.</>
              )}
            </div>

            <div
              style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16 }}
              data-testid="session-warning-modal-actions"
            >
              <button
                style={{width: '140px' }}
                className='red-button'
                onClick={expired ? redirectToLogin : signOut}
                data-testid={
                  expired
                    ? "session-warning-modal-login"
                    : "session-warning-modal-sign-out"
                }
              >
                {expired ? "Go to login" : "Sign out"}
              </button>
              {!expired && (
                <button
                  className="primary-button"
                  onClick={() => {
                    idleTimer.activate();
                    setShowWarning(false);
                    setCountdown(COUNTDOWN_TIME);
                  }}
                  data-testid="session-warning-modal-active"
                >
                  Active
                </button>
              )}
            </div>
          </div>
        </Modal>
      </StyledModal>
    </>
  );
};

export default SessionWarning;